import { merchantHoursRangeLabel } from '../../../shared/merchant-business-hours'

const LEGACY_ASSET_ORIGIN_PATTERN = /^https?:\/\/cos\.hayamagic\.com(?=\/|$)/i
const PUBLIC_ASSET_ORIGIN =
  'https://thecollected-1405947566.cos.ap-guangzhou.myqcloud.com'
const CONTROLLED_OSS_PREVIEW_PATTERN = /^\/(?:api\/)?suite\/oss\/files\/\d+\/(?:preview|download)(?:[?#].*)?$/i

export const resolveAssetUrl = (value: string) => {
  const normalized = value.trim()
  if (CONTROLLED_OSS_PREVIEW_PATTERN.test(normalized)) {
    return normalized.startsWith('/api/') ? normalized : `/api${normalized}`
  }
  return normalized.replace(LEGACY_ASSET_ORIGIN_PATTERN, PUBLIC_ASSET_ORIGIN)
}

export const resolveAssetUrlsDeep = <T>(value: T): T => {
  if (typeof value === 'string') return resolveAssetUrl(value) as T
  if (Array.isArray(value)) {
    return value.map((item) => resolveAssetUrlsDeep(item)) as T
  }
  if (!value || typeof value !== 'object') return value
  const prototype = Object.getPrototypeOf(value)
  if (prototype !== Object.prototype && prototype !== null) return value

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, resolveAssetUrlsDeep(item)]),
  ) as T
}

const collectAssetUrls = (value: unknown, urls: string[]) => {
  if (!value) return
  if (typeof value === 'string') {
    const normalized = value.trim()
    if (!normalized) return
    if (normalized.startsWith('[') || normalized.startsWith('{')) {
      try {
        collectAssetUrls(JSON.parse(normalized), urls)
        return
      } catch {
        // 兼容历史数据中直接保存的单个 URL。
      }
    }
    if (/^(https?:)?\/\//i.test(normalized) || normalized.startsWith('/')) {
      urls.push(resolveAssetUrl(normalized))
    }
    return
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectAssetUrls(item, urls))
    return
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    const asset = record.url ?? record.fileUrl ?? record.thumbUrl
    if (asset) collectAssetUrls(asset, urls)
    else Object.values(record).forEach((item) => collectAssetUrls(item, urls))
  }
}

export const parseAssetUrls = (...values: unknown[]) => {
  const urls: string[] = []
  values.forEach((value) => collectAssetUrls(value, urls))
  return [...new Set(urls)]
}

export const firstAssetUrl = (...values: unknown[]) => parseAssetUrls(...values)[0] || ''

export interface AssetItem {
  title: string
  urls: string[]
}

export const parseAssetItems = (value: unknown): AssetItem[] => {
  let source = value
  if (typeof source === 'string') {
    const normalized = source.trim()
    if (!normalized) return []
    try {
      source = JSON.parse(normalized)
    } catch {
      const urls = parseAssetUrls(normalized)
      return urls.length ? [{ title: '', urls }] : []
    }
  }
  const items = Array.isArray(source) ? source : [source]
  return items.flatMap((item) => {
    if (!item || typeof item !== 'object') {
      const urls = parseAssetUrls(item)
      return urls.length ? [{ title: '', urls }] : []
    }
    const record = item as Record<string, unknown>
    const urls = parseAssetUrls(record.files ?? record.images ?? record)
    return urls.length
      ? [{ title: String(record.title ?? record.name ?? '').trim(), urls }]
      : []
  })
}

/**
 * 历史公众号装修数据会把品牌名直接写进页面标题。H5 页面头部已经有统一的
 * 品牌视觉，因此展示菜单标题时只保留真正的业务名称。
 */
export const removeCollectedTitlePrefix = (value?: string) => {
  const normalized = value?.trim() || ''
  if (!normalized) return ''
  const title = normalized
    .replace(/^the\s*collected(?:\s*[·|｜:：\-—]\s*|\s*)/i, '')
    .trim()
  return title || normalized
}

export const toPlainText = (value?: string) => {
  if (!value) return ''
  const document = new DOMParser().parseFromString(value, 'text/html')
  document.querySelectorAll('script, style, template').forEach((element) => element.remove())
  return (document.body.textContent || '').replace(/\s+/g, ' ').trim()
}

export const toReadableText = (value?: string) => {
  if (!value) return ''
  const document = new DOMParser().parseFromString(value, 'text/html')
  document.querySelectorAll('script, style, template').forEach((element) => element.remove())
  document.querySelectorAll('br').forEach((element) => element.replaceWith('\n'))
  document
    .querySelectorAll('p, div, li, section, article, h1, h2, h3, h4, h5, h6')
    .forEach((element) => element.append('\n'))
  return (document.body.textContent || '')
    .replace(/\r/g, '')
    .replace(/[^\S\n]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export const toReadableLines = (value?: string) =>
  toReadableText(value)
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)

interface BusinessHourRange {
  close?: unknown
  open?: unknown
}

interface BusinessHourDay {
  allDay?: unknown
  closed?: unknown
  ranges?: unknown
}

export interface BusinessHourLine {
  label: string
  value: string
}

const BUSINESS_DAYS = [
  { key: 'monday', label: '周一' },
  { key: 'tuesday', label: '周二' },
  { key: 'wednesday', label: '周三' },
  { key: 'thursday', label: '周四' },
  { key: 'friday', label: '周五' },
  { key: 'saturday', label: '周六' },
  { key: 'sunday', label: '周日' },
] as const

const normalizeTime = (value: unknown) => {
  const time = String(value ?? '').trim()
  return /^\d{2}:\d{2}:00$/.test(time) ? time.slice(0, 5) : time
}

const formatDayHours = (value: BusinessHourDay) => {
  if (value.closed === true) return '休息'
  if (value.allDay === true) return '全天营业'
  if (!Array.isArray(value.ranges)) return '时间待确认'
  const ranges = (value.ranges as BusinessHourRange[])
    .map((range) => {
      const open = normalizeTime(range?.open)
      const close = normalizeTime(range?.close)
      return open && close ? merchantHoursRangeLabel(open, close) : ''
    })
    .filter(Boolean)
  return ranges.length ? ranges.join('、') : '时间待确认'
}

const formatDayLabel = (startIndex: number, endIndex: number) => {
  const start = BUSINESS_DAYS[startIndex]?.label || ''
  const end = BUSINESS_DAYS[endIndex]?.label || ''
  if (startIndex === endIndex) return start
  if (endIndex === startIndex + 1) return `${start}、${end}`
  return `${start}至${end}`
}

export const formatBusinessHours = (value: unknown): BusinessHourLine[] => {
  if (!value) return []
  let source: unknown = value
  if (typeof value === 'string') {
    const normalized = value.trim()
    if (!normalized) return []
    try {
      source = JSON.parse(normalized)
    } catch {
      return [{ label: '营业时间', value: toPlainText(normalized) }]
    }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return [{ label: '营业时间', value: toPlainText(String(value)) }]
  }

  const record = source as Record<string, BusinessHourDay>
  const days = BUSINESS_DAYS.flatMap((day, index) => {
    const schedule = record[day.key]
    return schedule && typeof schedule === 'object'
      ? [{ dayIndex: index, value: formatDayHours(schedule) }]
      : []
  })
  if (!days.length) {
    return [{ label: '营业时间', value: '时间待确认' }]
  }

  const lines: BusinessHourLine[] = []
  let groupStart = days[0]!
  let groupEnd = days[0]!
  for (const day of days.slice(1)) {
    if (day.dayIndex === groupEnd.dayIndex + 1 && day.value === groupEnd.value) {
      groupEnd = day
      continue
    }
    lines.push({
      label: formatDayLabel(groupStart.dayIndex, groupEnd.dayIndex),
      value: groupStart.value,
    })
    groupStart = day
    groupEnd = day
  }
  lines.push({
    label: formatDayLabel(groupStart.dayIndex, groupEnd.dayIndex),
    value: groupStart.value,
  })
  return lines
}
